import _str from "shared/util/_str";

import { fetchProfileFields, saveData } from "apps/profile_details/util/web";
import {
	setupFieldsFromApiResponse,
	getComment,
	validate,
	findAndHandleUnderageError,
	createExplicitlySetDetailsLookup,
} from "apps/profile_details/util/helpers";
import {
	MULTISELECT_FIELDS,
	FIELD_NAVIGATION,
	HEIGHT_RANGE,
	FIELDS_MAP,
	FIELD_PAIRS,
	UNIT_FIELDS,
	DEFAULT_DATA,
	BINARY_GENDERS,
	IN_SCHOOL_VALUES,
	COMPLETED_SCHOOL_VALUES,
	FULL_OR_PART_TIME,
	FREELANCE_OR_SELF,
	WANTS_MORE_CHILDREN_OPTIONS,
	WANTS_CHILDREN_OPTIONS,
	HAS_CHILDREN_VALUE,
	FIELDS_ORDER_EDUCATION_OCCUPATION,
	FIELDS_ORDER_ONBOARDING,
	FIELDS_ORDER_PROFILE_DETAILS,
} from "apps/profile_details/util/constants";
import { STEPS } from "apps/onboarding/util/constants";
import get from "lodash/get";


// ////////////////////////
// ACTION TYPES //////////
// //////////////////////

export const FETCH_FIELDS = "PROFILE_DETAILS.FETCH_FIELDS";
export const FETCH_FIELDS_SUCCESS = "PROFILE_DETAILS.FETCH_FIELDS_SUCCESS";
export const FETCH_FIELDS_ERROR = "PROFILE_DETAILS.FETCH_FIELDS_ERROR";

export const SET_UP_FIELDS = "PROFILE_DETAILS.SET_UP_FIELDS";
export const SET_CURRENT_INDEX = "PROFILE_DETAILS.SET_CURRENT_INDEX";
export const ADD_FIELD = "PROFILE_DETAILS.ADD_FIELD";
export const REMOVE_FIELD = "PROFILE_DETAILS.REMOVE_FIELD";
export const UPDATE_FIELD_DATA = "PROFILE_DETAILS.UPDATE_FIELD_DATA";
export const UPDATE_FIELD_CONFIG = "PROFILE_DETAILS.UPDATE_FIELD_CONFIG";
export const UPDATE_METRIC_PREFERENCE = "PROFILE_DETAILS.UPDATE_METRIC_PREFERENCE";

export const VALIDATE_FIELD = "PROFILE_DETAILS.VALIDATE_FIELD";
export const VALIDATE_FIELD_SUCCESS = "PROFILE_DETAILS.VALIDATE_FIELD_SUCCESS";
export const VALIDATE_FIELD_ERROR = "PROFILE_DETAILS.VALIDATE_FIELD_ERROR";

export const SAVE_FIELD_DATA = "PROFILE_DETAILS.SAVE_FIELD_DATA";
export const SAVE_FIELD_DATA_SUCCESS = "PROFILE_DETAILS.SAVE_FIELD_DATA_SUCCESS";
export const SAVE_FIELD_DATA_ERROR = "PROFILE_DETAILS.SAVE_FIELD_DATA_ERROR";

export const SKIP_FIELD = "PROFILE_DETAILS.SKIP_FIELD";
export const CLEAR_PROMPT_COMMENT = "PROFILE_DETAILS.CLEAR_PROMPT_COMMENT";


// ////////////////////////
// INITIAL STATE /////////
// //////////////////////

export const INITIAL_STATE = {
	apiData: null,
	userData: null,
	userFieldsData: null,
	isFetchingFields: false,
	fetchFieldError: null,
	fields: null,
	isMetric: null,
	isSaving: false,
	isLastField: false,
	isValidating: false,
	isStraightOnly: false,

	currentIndex: 0,

	currentField: null,
	errors: {},

	promptComment: null,
	feedback: {}, // perhaps this could be both error and success messages?
	suggestions: {},

	explicitlySetDetailsLookup: {},
};


// ////////////////////////
// REDUCER ///////////////
// //////////////////////

export function reducer(state = INITIAL_STATE, action) {
	switch (action.type) {
		case FETCH_FIELDS: {
			return {
				...state,
				isFetchingFields: true,
			};
		}

		case FETCH_FIELDS_SUCCESS: {
			// set up fields
			const {
				apiData,
				userData,
				userFieldsData,
				explicitlySetDetails,
			} = action;

			const explicitlySetDetailsLookup = createExplicitlySetDetailsLookup(explicitlySetDetails);
			const fields = setupFieldsFromApiResponse(
				apiData, userFieldsData, explicitlySetDetailsLookup,
			);

			return {
				...state,
				apiData,
				userData,
				userFieldsData,
				isMetric: userFieldsData.metric === 1,
				isStraightOnly: false,
				fields,
				isFetchingFields: false,
				feedback: {},
				suggestions: {},
				explicitlySetDetailsLookup,
			};
		}

		case FETCH_FIELDS_ERROR: {
			return {
				...state,
				fetchFieldError: action.error,
				isFetchingFields: false,
				feedback: {},
				suggestions: {},
			};
		}

		case SET_UP_FIELDS: {
			const { currentIndex, fieldOrder } = action.payload;

			return {
				...state,
				fieldOrder,
				currentIndex,
				currentField: fieldOrder[currentIndex],
				isLastField: currentIndex === fieldOrder.length - 1 || fieldOrder.length === 0,
			};
		}

		case ADD_FIELD: {
			const fieldOrder = [...state.fieldOrder];
			const { currentField } = state;
			const currentFieldIndex = fieldOrder.indexOf(currentField);

			// short circuit if the field already exists
			if (fieldOrder.indexOf(action.fieldName) >= 0) {
				return { ...state };
			}

			fieldOrder.splice(currentFieldIndex + 1, 0, action.fieldName);

			return {
				...state,
				fieldOrder,
			};
		}

		case REMOVE_FIELD: {
			const fieldOrder = [...state.fieldOrder].filter((field) => {
				return field !== action.fieldName;
			});

			return {
				...state,
				fieldOrder,
			};
		}

		case SET_CURRENT_INDEX: {
			const { direction } = action;
			const { fieldOrder } = state;

			let isLastField = false;
			let currentIndex = state.currentIndex;

			if (
				direction === FIELD_NAVIGATION.NEXT &&
				currentIndex < state.fieldOrder.length - 1
			) {
				currentIndex += 1;
			} else if (
				direction === FIELD_NAVIGATION.PREVIOUS &&
				currentIndex > 0
			) {
				currentIndex -= 1;
			}

			if (currentIndex === fieldOrder.length - 1) {
				isLastField = true;
			}

			return {
				...state,
				currentIndex,
				isLastField,
				promptComment: null,
				currentField: state.fieldOrder[currentIndex],
			};
		}

		case UPDATE_FIELD_DATA: {
			let {
				fields, promptComment, errors, feedback, suggestions,
			} = state;
			const { fieldName, data } = action;

			errors = {
				...errors,
				[fieldName]: false,
			};

			feedback = {
				...feedback,
				[fieldName]: "",
			};

			suggestions = {
				...suggestions,
				[fieldName]: false,
			};

			fields = {
				...fields,
				[fieldName]: {
					...fields[fieldName],
					data,
					isSkipped: false,
				},
			};

			promptComment = getComment(fieldName, data);

			return {
				...state,
				fields,
				promptComment,
				errors,
				suggestions,
				feedback,
			};
		}

		case UPDATE_FIELD_CONFIG: {
			const { fields } = state;
			const { payload: { fieldName, fieldData } } = action;

			return {
				...state,
				fields: {
					...fields,
					[fieldName]: {
						...fields[fieldName],
						...fieldData,
					},
				},
			};
		}

		case VALIDATE_FIELD: {
			return {
				...state,
				isValidating: true,
			};
		}

		case VALIDATE_FIELD_SUCCESS: {
			let { errors, feedback, suggestions } = state;

			errors = {
				...errors,
				[action.currentField]: false,
			};

			feedback = {
				...feedback,
				[action.currentField]: action.feedback,
			};

			suggestions = {
				...suggestions,
				[action.currentField]: false,
			};

			return {
				...state,
				errors,
				feedback,
				suggestions,
				isValidating: false,
			};
		}

		case VALIDATE_FIELD_ERROR: {
			let { errors, suggestions, feedback } = state;

			errors = {
				...errors,
				[action.currentField]: true,
			};

			feedback = {
				...feedback,
				[action.currentField]: action.feedback,
			};

			suggestions = {
				...suggestions,
				[action.currentField]: action.suggestions,
			};

			return {
				...state,
				errors,
				suggestions,
				feedback,
				isValidating: false,
			};
		}

		case UPDATE_METRIC_PREFERENCE: {
			// set the new isMetric to the opposite of what it was
			const isMetric = !state.isMetric;
			let { fields } = state;

			// update all unit related field options. (for now, just height)
			// reset data, since there's no 1 to 1 mapping for the dropdown values
			fields = {
				...fields,
				[FIELDS_MAP.HEIGHT]: {
					...fields[FIELDS_MAP.HEIGHT],
					options: isMetric ? HEIGHT_RANGE.METRIC : HEIGHT_RANGE.IMPERIAL,
					data: "",
				},
			};

			return {
				...state,
				isMetric,
				fields,
			};
		}

		case SAVE_FIELD_DATA: {
			return {
				...state,
				isSaving: true,
			};
		}

		case SAVE_FIELD_DATA_SUCCESS: {
			// set up fields

			return {
				...state,
				isSaving: false,
			};
		}

		case SAVE_FIELD_DATA_ERROR: {
			let errors = state.errors;

			errors = {
				...errors,
				[state.currentField]: true,
			};

			return {
				...state,
				errors,
				isSaving: false,
			};
		}

		case SKIP_FIELD: {
			let { fields, promptComment, errors } = state;
			const { currentField } = state;
			let isSkipped;

			// if it's an array, set each field to skipped
			if (Array.isArray(currentField)) {
				// if any of the fields are skipped, unskip all fields in the array
				const hasSkipped = currentField.some((field) => {
					return fields[field].isSkipped;
				});

				isSkipped = !hasSkipped;

				currentField.forEach((field) => {
					fields = {
						...fields,
						[field]: {
							...fields[field],
							isSkipped,
							data: MULTISELECT_FIELDS.includes(field) ? [] : "",
						},
					};

					errors = {
						...errors,
						[field]: false,
					};
				});
			} else {
				isSkipped = !fields[currentField].isSkipped;

				fields = {
					...fields,
					[currentField]: {
						...fields[currentField],
						isSkipped,
						data: DEFAULT_DATA[currentField] || "",
					},
				};

				errors = {
					...state.errors,
					[currentField]: false,
				};
			}

			promptComment = getComment("skip", isSkipped);

			return {
				...state,
				fields,
				promptComment,
				errors,
			};
		}

		case CLEAR_PROMPT_COMMENT: {
			return {
				...state,
				promptComment: null,
			};
		}

		default:
			return state;
	}
}


// ////////////////////////
// ACTION CREATORS ///////
// //////////////////////

export function setUpFields({
	order,
	showLastStep = false,
	filterOutCompletedSteps = false,
} = {}) {
	return (dispatch, getStore) => {
		const { explicitlySetDetailsLookup } = getStore().profileDetails;
		const orderLength = order.length;
		let fieldOrder = order;

		if (filterOutCompletedSteps) {
			fieldOrder = fieldOrder.filter((field) => !explicitlySetDetailsLookup[field]);
		}

		dispatch({
			type: SET_UP_FIELDS,
			payload: {
				fieldOrder,
				currentIndex: showLastStep ? orderLength - 1 : 0,
			},
		});
	};
}

export function fetchFields(userid, detailStep, showLastStep, hasMockedOnboarding = false) {
	return (dispatch) => {
		dispatch({
			type: FETCH_FIELDS,
		});

		fetchProfileFields(userid)
			.then((res) => {
				const userFieldsData = get(res, "user.details.api_values");
				const isProfileDetails = detailStep === STEPS.PROFILE_DETAILS;

				if (!res.parameters || !userFieldsData || !res.wiw) {
					dispatch({
						type: FETCH_FIELDS_ERROR,
						error: _str("Oops, womething went wrong!"),
					});

					return;
				}

				// though it's not a profile detail, in that it gets saved via a put to
				// `/profile/me` like the others, we modify Name with this app.
				userFieldsData.name = get(res, "user.userinfo.realname") || "";

				dispatch({
					type: FETCH_FIELDS_SUCCESS,
					apiData: res.parameters,

					// only set explicitlySetDetails for details reboarding
					// we don't want fields to be marked as skipped in regular onboarding
					explicitlySetDetails: isProfileDetails ? get(res, "explicitlySetDetails", []) : [],
					userData: res.user,
					userFieldsData,
				});

				let fieldOrder = FIELDS_ORDER_ONBOARDING;

				if (detailStep === STEPS.EDUCATION_OCCUPATION) {
					fieldOrder = FIELDS_ORDER_EDUCATION_OCCUPATION;
				} else if (detailStep === STEPS.PROFILE_DETAILS) {
					fieldOrder = FIELDS_ORDER_PROFILE_DETAILS;
				}

				// set up field order
				dispatch(setUpFields({
					order: hasMockedOnboarding ? [FIELDS_MAP.LOCATION] : fieldOrder,
					showLastStep,
					filterOutCompletedSteps: detailStep === STEPS.PROFILE_DETAILS,
				}));
			})
			.catch((err) => {
				dispatch({
					type: FETCH_FIELDS_ERROR,
					error: err,
				});

				console.warn("Fetch fields error:", err);
			});
	};
}

export function setCurrentIndex(direction) {
	return {
		type: SET_CURRENT_INDEX,
		direction,
	};
}

export function updateFieldData(fieldName, data) {
	return {
		type: UPDATE_FIELD_DATA,
		fieldName,
		data,
	};
}

export function updateMetricPreference() {
	return {
		type: UPDATE_METRIC_PREFERENCE,
	};
}

export function startValidation() {
	return {
		type: VALIDATE_FIELD,
	};
}

export function validateField() {
	return (dispatch, getStore) => {
		dispatch({
			type: VALIDATE_FIELD,
		});

		const { currentField, fields } = getStore().profileDetails;

		validate(
			currentField,
			fields[currentField].data,

			// success func
			(success, updatedValue) => {
				if (updatedValue) {
					dispatch({
						type: UPDATE_FIELD_DATA,
						fieldName: currentField,
						data: {
							...fields[currentField].data,
							...updatedValue,
						},
					});
				}

				dispatch({
					type: VALIDATE_FIELD_SUCCESS,
					currentField,
					feedback: success,
				});
			},

			// error func
			(error) => {
				dispatch({
					type: VALIDATE_FIELD_ERROR,
					currentField,
					feedback: error,
				});
			},

			// suggestion
			(error, suggestions) => {
				dispatch({
					type: VALIDATE_FIELD_ERROR,
					currentField,
					feedback: error,
					suggestions,
				});
			},
		);
	};
}

export function formatCurrentFieldData({ fields, isMetric, currentField, isStraightOnly }) {
	const data = {};


	if (Array.isArray(currentField)) {
		currentField.forEach((field) => {
			data[fields[field].name] = fields[field].data;
		});
	} else if (currentField === FIELDS_MAP.LANGUAGES) {
		// TODO temporary (or permanent) fix for saving languages
		// becuase with the existing api they require a fluency
		// but now we are only showing languages and not fluency!
		data.languages = (fields[currentField].data || [])
			.map((language) => ({ language, fluency: "&mdash;" }));
	} else if (currentField === FIELDS_MAP.RELIGION_VALUE) {
		// TODO temporary (or permanent) fix for saving religion
		// becuase with the existing api they require a modifier
		// but now we are only showing relgion values with no modifiers
		data.religion = {
			value: fields[currentField].data || "&mdash;",
			modifier: "&mdash;",
		};
	} else if (currentField === FIELDS_MAP.EDUCATION_SCHOOL) {
		// data is "" when prefer not to say is selected
		// schools gets saved to /profile/me/education if it is present
		const { schools } = fields[currentField].data || { schools: [] };
		data.education = {
			// currently the backend requires us to save education value and modifier to save schools
			...(fields[FIELDS_MAP.EDUCATION_LEVEL_AND_SCHOOL].data || {}),
			schools,
		};
	} else if (currentField === FIELDS_MAP.EDUCATION_LEVEL) {
		// data is "&mdash;" when prefer not to say is selected
		const educationLevel = fields[currentField].data;
		data[FIELDS_MAP.EDUCATION_LEVEL] = educationLevel === "&mdash;" ? "0" : educationLevel;
	} else if (currentField === FIELDS_MAP.OCCUPATION_TITLE) {
		// OCCUPATION_TITLE saves its data under occupation
		const { title } = fields[currentField].data;
		data.occupation = {
			title,
		};
	} else if (currentField.includes(".")) {
		// can't submit modifier without value, vice versa
		const fieldNameBase = currentField.split(".")[0];

		FIELD_PAIRS[fieldNameBase].forEach((field) => {
			data[fields[field].name] = fields[field].data;
		});
	} else {
		data[fields[currentField].name] = fields[currentField].data;
	}

	if (UNIT_FIELDS.includes(currentField)) {
		data.isMetric = isMetric;
	}

	if (currentField === FIELDS_MAP.GENDER_TAGS) {
		data.isStraightOnly = isStraightOnly;
	}

	return data;
}

export function toggleConditionalFields() {
	return (dispatch, getStore) => {
		const { fields, currentField, apiData } = getStore().profileDetails;

		// if they've selceted more than one gender add in the underlying gender
		// field or pronouns, otherwise make sure it doesn't show for binary folks.
		if (
			currentField === FIELDS_MAP.GENDER_TAGS
			|| (currentField === FIELDS_MAP.UNDERLYING_GENDER && apiData.pronouns)
		) {
			const hasMoreThanOneGender = fields[FIELDS_MAP.GENDER_TAGS].data.length > 1 ||
				!BINARY_GENDERS[fields[FIELDS_MAP.GENDER_TAGS].data[0]];
			const type = hasMoreThanOneGender ? ADD_FIELD : REMOVE_FIELD;

			dispatch({
				type,
				fieldName: FIELDS_MAP.UNDERLYING_GENDER,
			});
		}

		// in details reboarding, if the user selects they are in a school
		// then add the school field, otherwise remove it
		if (currentField === FIELDS_MAP.EDUCATION_LEVEL) {
			const educationLevel = fields[FIELDS_MAP.EDUCATION_LEVEL].data;
			const showSchoolField =
				COMPLETED_SCHOOL_VALUES.has(educationLevel)
				|| IN_SCHOOL_VALUES.has(educationLevel);

			const type = showSchoolField
				? ADD_FIELD : REMOVE_FIELD;

			dispatch({
				type,
				fieldName: FIELDS_MAP.EDUCATION_SCHOOL,
			});
		}

		// in details reboarding, if the user selects they are employed
		// then we need to do some fancy stuff
		if (currentField === FIELDS_MAP.EMPLOYMENT) {
			const employmentStatus = fields[FIELDS_MAP.EMPLOYMENT].data;

			// it is ok to call add field if the field is already added, there is a check in the reducer
			if (FULL_OR_PART_TIME.has(employmentStatus)) {
				dispatch({
					type: ADD_FIELD,
					fieldName: FIELDS_MAP.OCCUPATION,
				});
			} else if (!FULL_OR_PART_TIME.has(employmentStatus)) {
				dispatch({
					type: REMOVE_FIELD,
					fieldName: FIELDS_MAP.OCCUPATION,
				});
			}

			if (FREELANCE_OR_SELF.has(employmentStatus)) {
				dispatch({
					type: ADD_FIELD,
					fieldName: FIELDS_MAP.OCCUPATION_TITLE,
				});
			} else if (!FREELANCE_OR_SELF.has(employmentStatus)) {
				dispatch({
					type: REMOVE_FIELD,
					fieldName: FIELDS_MAP.OCCUPATION_TITLE,
				});
			}
		}

		// in details reboarding, if the user selects they have kids
		// then we need to update the labels for wants kids -> wants more kids
		if (currentField === FIELDS_MAP.CHILDREN_HAVE) {
			const options = fields[currentField].data === HAS_CHILDREN_VALUE ?
				WANTS_MORE_CHILDREN_OPTIONS : WANTS_CHILDREN_OPTIONS;

			dispatch({
				type: UPDATE_FIELD_CONFIG,
				payload: {
					fieldName: FIELDS_MAP.CHILDREN_WANT,
					fieldData: { options },
				},
			});
		}
	};
}

export function saveFieldData() {
	return (dispatch, getStore) => {
		dispatch({
			type: SAVE_FIELD_DATA,
		});

		const { fields, isMetric, currentField, isStraightOnly } = getStore().profileDetails;

		const currentFieldData = formatCurrentFieldData({
			fields, isMetric, currentField, isStraightOnly,
		});

		dispatch(toggleConditionalFields());

		saveData(currentFieldData)
			.then((res) => {
				if (res.error) {
					dispatch({
						type: SAVE_FIELD_DATA_ERROR,
						error: res.error,
					});
				} else {
					// search for and handle an underage error
					findAndHandleUnderageError(res);

					dispatch({
						type: SAVE_FIELD_DATA_SUCCESS,
					});

					dispatch({
						type: SET_CURRENT_INDEX,
						direction: FIELD_NAVIGATION.NEXT,
					});
				}
			})
			.catch((err) => {
				dispatch({
					type: SAVE_FIELD_DATA_ERROR,
					error: err,
				});
			});
	};
}

export function skipField() {
	return {
		type: SKIP_FIELD,
	};
}

export function clearPromptComment() {
	return {
		type: CLEAR_PROMPT_COMMENT,
	};
}



// WEBPACK FOOTER //
// ./src/ducks/profile_details.js